import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Instagram, Facebook, Twitter, Linkedin, MessageCircle } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/motion-primitives";
import ConsultForm from "@/components/ConsultForm";
import { CONTACT, SOCIALS } from "@/data/content";

const SOCIAL_ICONS = { Instagram, Facebook, "Twitter / X": Twitter, LinkedIn: Linkedin };

const ContactHero = () => (
  <section className="relative overflow-hidden bg-navy pb-24 pt-40 text-white" data-testid="contact-hero">
    <div className="noise-overlay absolute inset-0" aria-hidden="true" />
    <div className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-6">
      <MaskedLine delay={0.9}>
        <span className="font-accent text-xs font-semibold uppercase tracking-[0.3em] text-gold">Contact Us</span>
      </MaskedLine>
      <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
        <MaskedLine delay={1.0}>Let's plan your</MaskedLine>
        <MaskedLine delay={1.12}><span className="italic text-gold">journey abroad.</span></MaskedLine>
      </h1>
      <MaskedLine delay={1.3}>
        <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
          Fill the form and your enquiry lands directly in our counselors' inbox — with a pre-filled WhatsApp chat opening right after.
        </p>
      </MaskedLine>
    </div>
  </section>
);

const DetailRow = ({ icon: Icon, label, children, testid }) => (
  <div className="flex gap-4" data-testid={testid}>
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </span>
    <div>
      <p className="font-accent text-xs font-bold uppercase tracking-widest text-muted">{label}</p>
      <div className="mt-1.5 text-base leading-relaxed text-navy">{children}</div>
    </div>
  </div>
);

const ContactBody = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
    <div className="grid gap-16 lg:grid-cols-12">
      <FadeUp className="lg:col-span-7">
        <div className="rounded-xl border border-line bg-white p-8 shadow-soft md:p-12" data-testid="contact-form-card">
          <h2 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">
            Book your <span className="italic text-gold">free consultation</span>
          </h2>
          <p className="mt-3 text-base text-muted">All fields marked * are required. We respond within 24 hours.</p>
          <div className="mt-8">
            <ConsultForm />
          </div>
        </div>
      </FadeUp>

      <FadeUp delay={0.15} className="lg:col-span-5">
        <div className="space-y-8 rounded-xl border border-line bg-white p-8 shadow-soft md:p-10" data-testid="contact-details-card">
          <DetailRow icon={MapPin} label="Visit Us" testid="contact-address">
            {CONTACT.address}
            <a
              href={CONTACT.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-maps-link"
              className="mt-1 inline-flex items-center gap-1 font-accent text-sm font-semibold text-gold transition-colors hover:text-navy"
            >
              Click here for link <ArrowUpRight className="h-4 w-4" />
            </a>
          </DetailRow>
          <DetailRow icon={Phone} label="Call / WhatsApp" testid="contact-phones">
            <a href={`tel:${CONTACT.phonePrimary.replace(/\s/g, "")}`} data-testid="contact-phone-primary" className="block transition-colors hover:text-gold">{CONTACT.phonePrimary}</a>
            <a href={`tel:${CONTACT.phoneSecondary.replace(/\s/g, "")}`} data-testid="contact-phone-secondary" className="block transition-colors hover:text-gold">{CONTACT.phoneSecondary}</a>
          </DetailRow>
          <DetailRow icon={Mail} label="Email" testid="contact-emails">
            <a href={`mailto:${CONTACT.emailPrimary}`} data-testid="contact-email-primary" className="block break-all transition-colors hover:text-gold">{CONTACT.emailPrimary}</a>
            <a href={`mailto:${CONTACT.emailSecondary}`} data-testid="contact-email-secondary" className="block break-all transition-colors hover:text-gold">{CONTACT.emailSecondary}</a>
          </DetailRow>
          <DetailRow icon={Clock} label="Office Hours" testid="contact-hours">
            {CONTACT.hours}
            <span className="block text-sm text-muted">Sunday: Closed</span>
          </DetailRow>

          <div className="border-t border-line pt-6">
            <p className="font-accent text-xs font-bold uppercase tracking-widest text-muted">Follow Us</p>
            <div className="mt-4 flex gap-3">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.name];
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`contact-social-${s.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-navy transition-all duration-300 hover:border-gold hover:bg-gold hover:text-navy"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-gold hover:text-navy"
            >
              <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

const MapSection = () => (
  <section className="border-t border-line bg-white py-24 md:py-32" data-testid="map-section">
    <div className="mx-auto max-w-7xl px-6">
      <FadeUp className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Find Us</p>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
            In the heart of <span className="italic text-gold">Mehdipatnam</span>
          </h2>
        </div>
        <p className="max-w-sm text-base leading-relaxed text-muted">
          Beside St. Ann's College for Women — easy to reach from anywhere in Hyderabad.
        </p>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-14">
        <div className="overflow-hidden rounded-xl border border-line shadow-soft">
          <iframe
            title="Student First Overseas Consultants location on Google Maps"
            data-testid="google-maps-embed"
            src={CONTACT.mapsEmbed}
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <a
          href={CONTACT.mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="map-directions-link"
          className="mt-5 inline-flex items-center gap-1 font-accent text-sm font-semibold text-gold transition-colors hover:text-navy"
        >
          Click here for link <ArrowUpRight className="h-4 w-4" />
        </a>
      </FadeUp>
    </div>
  </section>
);

export default function Contact() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="contact-page"
    >
      <ContactHero />
      <ContactBody />
      <MapSection />
    </motion.div>
  );
}
